// Authenticated viewer identity, provided once at the top of the
// `(authed)` layout and consumed everywhere downstream via `useViewer()`.
//
// Why a context rather than a per-call prop or a `qc.getQueryData()`
// cache read?
//
//   1. The viewer's id is global, session-bound state — every authed
//      screen needs it. Prop-drilling N components deep for a value
//      that never changes during the session is structural noise.
//   2. The hook can enforce "must be inside <ViewerProvider>" at the
//      type/runtime boundary, which makes "no viewer in this tree"
//      an impossible-state-unrepresentable invariant (CLAUDE.md §4)
//      rather than a silent `undefined` that lets bugs through.
//   3. Most importantly: optimistic mutations need the viewer at
//      construction time. The white-bubble-then-black-bubble glitch
//      in `useSendMessage` came from a synthesized MessageView with
//      `senderUserId: ''`, which `mapMessage` then rendered as
//      "from peer." Any optimistic entity whose view-model logic
//      branches on author (isMe, alignment, photo, "you said …") MUST
//      be authored with the viewer's real identity — this hook is the
//      single source of truth for that.
//
// Hydration: the `_authed` layout resolves the session, builds a
// `ViewerIdentity`, and passes it as the `value` prop of `<ViewerProvider>`. Components rendered below — at
// any depth — read it via `useViewer()` / `useViewerId()`.
//
// Pure logic (mappers, plain functions) should continue to take a
// `viewerId` parameter rather than reaching into context: React
// context is a UI-tree concern, not a pure-function concern. The
// boundary is the component that calls the pure function — that's
// where the hook is invoked.

import {
  createContext,
  useContext,
  type ReactNode,
} from 'react';
import type { UserId } from '@leanin/shared/ids';

export interface ViewerIdentity {
  readonly id: UserId;
  readonly displayName: string;
  readonly photoUrl: string | null;
}

const ViewerContext = createContext<ViewerIdentity | null>(null);

export function ViewerProvider({
  value,
  children,
}: {
  // `null` is reserved for the public layout (`_public.tsx`) where the
  // visitor isn't signed in. Authed-only callers using `useViewer()`
  // will throw if the provider is null — the explicit contract.
  value: ViewerIdentity | null;
  children: ReactNode;
}) {
  return (
    <ViewerContext.Provider value={value}>{children}</ViewerContext.Provider>
  );
}

export function useViewer(): ViewerIdentity {
  const ctx = useContext(ViewerContext);
  if (ctx === null) {
    throw new Error(
      'useViewer must be used within an authed <ViewerProvider>. The _authed layout wraps every authed route — this error means the call site rendered outside it (or under the public layout, which provides a null viewer).',
    );
  }
  return ctx;
}

/**
 * Public-layout-friendly variant. Returns `null` when the visitor is
 * anonymous (no Clerk session) and the identity otherwise. Use this in
 * components that can render under either `_authed` or `_public`.
 */
export function useOptionalViewer(): ViewerIdentity | null {
  return useContext(ViewerContext);
}

export function useViewerId(): UserId {
  return useViewer().id;
}
